import { Controller } from 'stimulus';
import Chart from 'chart.js/auto';
import { ComparatorColorPool } from './comparator_color_pool';
import { renderCompareChips } from './compare_chip_strip';
import { currentTheme } from './chart_controller/plugins/background_color_plugin';

// Shared chart palette, matched to the per-org activities chart
// (`summary_charts/activities_chart_controller.js`) so every chart in the
// organisation-groups section reads with the same grid / label / legend
// colours in both themes.
const COLOR_BLACK = '#000';
const COLOR_GRAY = '#AAAFB5';
const COLOR_GRAY_LIGHT = '#E5E5E5';
const COLOR_GRAY_DARK = '#6A6A6A';

// Abstract base class shared by the organisation-group comparison charts
// (response_times, sleep_data, support_entries). Owns the comparator
// selection state, URL persistence, swatch painting, and the theme-change
// lifecycle. Subclasses must implement:
//
//   initChart()        — create `this.chart` (Chart.js instance)
//   paintChartColors() — apply colours to chart datasets
//   compareIds         — getter returning a Set of legal comparator ids
//
// Subclasses may override `applyVisibility()` for non-default visibility
// rules (e.g. layered datasets in response_times).
//
// Not registered as its own Stimulus controller — the file deliberately
// omits the `_controller.js` suffix used by the auto-registration glob.
export default class CompareChartBase extends Controller {
  static targets = [
    'canvas',
    'compareCheckbox',
    'colorSwatch',
    'compareSummary',
    'compareList',
    'periodLink',
    'compareField',
  ];
  static values = { chartData: Object, imageFilename: String };

  connect() {
    this.selectedItems = new Set();
    this.colorPool = new ComparatorColorPool();
    document.addEventListener('amba:theme_changed', this.handleThemeChange);
    // The comparison table may live in a separate turbo frame (response_times),
    // so accept toggles and sync requests via window events too.
    window.addEventListener('compare:toggle', this.onCompareToggle);
    window.addEventListener('compare:sync-request', this.onCompareSyncRequest);
    this.restoreCompareFromUrl();
    this.initChart();
    this.applyVisibility({ update: false });
    this.applyColors({ update: false });
    this.chart?.update('none');
    this.syncCheckboxes();
    this.updatePersistedCompare();
    this.renderCompareSummary();
    this.broadcastCompareState();
  }

  // When the comparison table is a Tabulator (e.g. support_entries), expanding
  // a row rebuilds the table body from the source HTML — which has no
  // checked/colour state — recreating these targets. Re-apply the live
  // selection as each target (re)connects so an expand can't visually clear a
  // ticked comparator. Guarded until connect() has set up the selection state;
  // the initial targets are handled by connect()'s syncCheckboxes()/applyColors().
  compareCheckboxTargetConnected(checkbox) {
    if (!this.selectedItems) return;
    checkbox.checked = this.selectedItems.has(checkbox.dataset.compareId);
  }

  colorSwatchTargetConnected(swatch) {
    if (!this.colorPool) return;
    this.paintSwatch(swatch);
  }

  disconnect() {
    document.removeEventListener('amba:theme_changed', this.handleThemeChange);
    window.removeEventListener('compare:toggle', this.onCompareToggle);
    window.removeEventListener(
      'compare:sync-request',
      this.onCompareSyncRequest
    );
    this.chart?.destroy();
  }

  handleThemeChange = () => {
    this.initChart();
    this.applyVisibility({ update: false });
    this.applyColors({ update: false });
    this.chart?.update('none');
  };

  // ── Subclass contract ─────────────────────────────────────────

  initChart() {
    throw new Error(`${this.identifier}: initChart() must be implemented`);
  }

  paintChartColors() {
    throw new Error(
      `${this.identifier}: paintChartColors() must be implemented`
    );
  }

  get compareIds() {
    throw new Error(
      `${this.identifier}: compareIds getter must be implemented`
    );
  }

  // ── shared theme ──────────────────────────────────────────────

  // Theme-aware colours shared by every organisation-groups chart, matched
  // to the activities chart palette. Returns grid / axis-label / legend
  // colours for the current theme.
  chartTheme() {
    Chart.defaults.font.family = "'Public Sans', sans-serif";
    const light = currentTheme() === 'light';

    return {
      gridColor: light ? COLOR_GRAY_LIGHT : COLOR_GRAY_DARK,
      mutedColor: light ? COLOR_GRAY_DARK : COLOR_GRAY,
      legendColor: light ? COLOR_BLACK : COLOR_GRAY,
    };
  }

  // ── image export ──────────────────────────────────────────────

  // Download the chart as a PNG. Chart.js canvases are transparent, which
  // reads badly pasted into a slide or doc, so composite the chart onto the
  // panel's background colour first — keeping it WYSIWYG in both light and
  // dark themes. Filename is the `imageFilename` value plus today's date.
  downloadImage() {
    if (!this.chart) return;

    const source = this.canvasTarget;
    const canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height;

    const context = canvas.getContext('2d');
    context.fillStyle = this.chartBackgroundColor();
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0);

    const base = this.imageFilenameValue || 'chart';
    const date = new Date().toISOString().slice(0, 10);
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `${base}-${date}.png`;
    link.click();
  }

  // The first opaque background colour walking up from the canvas (the chart
  // panel's card), defaulting to white — so the export matches what's on
  // screen instead of rendering onto a transparent void.
  chartBackgroundColor() {
    let node = this.canvasTarget;
    while (node) {
      const color = getComputedStyle(node).backgroundColor;
      if (
        color &&
        color !== 'transparent' &&
        !color.startsWith('rgba(0, 0, 0, 0')
      ) {
        return color;
      }
      node = node.parentElement;
    }
    return '#ffffff';
  }

  // ── URL persistence ───────────────────────────────────────────

  restoreCompareFromUrl() {
    const compare = new URLSearchParams(window.location.search).get('compare');
    if (!compare) return;

    const valid = this.compareIds;
    for (const id of compare.split(',').filter(Boolean)) {
      if (id !== 'aggregate' && valid.has(id)) {
        this.selectedItems.add(id);
        this.colorPool.assign(id);
      }
    }
  }

  updatePersistedCompare() {
    const compareParam =
      this.selectedItems.size > 0
        ? Array.from(this.selectedItems).join(',')
        : null;

    const url = new URL(window.location);
    if (compareParam) url.searchParams.set('compare', compareParam);
    else url.searchParams.delete('compare');
    history.replaceState(null, '', url);

    this.periodLinkTargets.forEach((link) => {
      const linkUrl = new URL(link.href);
      if (compareParam) linkUrl.searchParams.set('compare', compareParam);
      else linkUrl.searchParams.delete('compare');
      link.href = linkUrl.toString();
    });

    if (this.hasCompareFieldTarget) {
      this.compareFieldTarget.value = compareParam || '';
    }
  }

  // ── compare toggling ──────────────────────────────────────────

  toggleCompare(event) {
    const compareId = event.currentTarget.dataset.compareId;
    if (!compareId) return;

    if (event.currentTarget.checked) {
      this.selectedItems.add(compareId);
      this.colorPool.assign(compareId);
    } else {
      this.selectedItems.delete(compareId);
      this.colorPool.release(compareId);
    }

    this.afterSelectionChanged();
  }

  removeCompare(event) {
    const compareId = event.currentTarget.dataset.compareId;
    if (!compareId) return;

    this.selectedItems.delete(compareId);
    this.colorPool.release(compareId);

    this.afterSelectionChanged();
  }

  clearAllCompare() {
    if (this.selectedItems.size === 0) return;

    for (const id of Array.from(this.selectedItems)) {
      this.colorPool.release(id);
    }
    this.selectedItems.clear();

    this.afterSelectionChanged();
  }

  afterSelectionChanged() {
    this.applyVisibility({ update: false });
    this.applyColors({ update: false });
    this.chart?.update('none');
    this.syncCheckboxes();
    this.updatePersistedCompare();
    this.renderCompareSummary();
    this.broadcastCompareState();
  }

  // ── cross-frame compare (table in a separate turbo frame) ─────

  onCompareToggle = (event) => {
    const { compareId, checked } = event.detail || {};
    if (!compareId || !this.compareIds.has(compareId)) return;

    if (checked) {
      this.selectedItems.add(compareId);
      this.colorPool.assign(compareId);
    } else {
      this.selectedItems.delete(compareId);
      this.colorPool.release(compareId);
    }

    this.afterSelectionChanged();
  };

  onCompareSyncRequest = () => {
    this.broadcastCompareState();
  };

  broadcastCompareState() {
    const colors = {};
    this.selectedItems.forEach((id) => {
      colors[id] = this.colorPool.get(id);
    });

    window.dispatchEvent(
      new CustomEvent('compare:state', {
        detail: { selectedIds: Array.from(this.selectedItems), colors },
      })
    );
  }

  // ── DOM sync ──────────────────────────────────────────────────

  syncCheckboxes() {
    this.compareCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = this.selectedItems.has(checkbox.dataset.compareId);
    });
  }

  renderCompareSummary() {
    if (!this.hasCompareSummaryTarget || !this.hasCompareListTarget) return;

    renderCompareChips({
      summary: this.compareSummaryTarget,
      list: this.compareListTarget,
      selectedItems: this.selectedItems,
      colorPool: this.colorPool,
      labelFor: (id) => this.compareLabelFor(id),
      removeAction: `${this.identifier}#removeCompare`,
    });
  }

  compareLabelFor(compareId) {
    if (!this.chart) return compareId;
    const dataset = this.chart.data.datasets.find(
      (d) => d.compareId === compareId
    );
    if (!dataset) return compareId;
    return dataset.compareLabel || dataset.label || compareId;
  }

  applyVisibility({ update = true } = {}) {
    if (!this.chart) return;

    const hasSelections = this.selectedItems.size > 0;

    this.chart.data.datasets.forEach((dataset, index) => {
      const visible =
        dataset.compareId === 'aggregate'
          ? !hasSelections
          : this.selectedItems.has(dataset.compareId);
      this.chart.setDatasetVisibility(index, visible);
    });

    if (update) this.chart.update();
  }

  applyColors({ update = true } = {}) {
    this.applySwatchColors();
    if (!this.chart) return;
    this.paintChartColors();
    if (update) this.chart.update();
  }

  applySwatchColors() {
    this.colorSwatchTargets.forEach((swatch) => this.paintSwatch(swatch));
  }

  paintSwatch(swatch) {
    const color = this.colorPool.get(swatch.dataset.compareId);
    swatch.style.backgroundColor = color || '';
    swatch.classList.toggle('is-empty', !color);
  }
}
